import { useRequiredContext } from "@redotech/react-util/context";
import {
  type SplitShipmentDiscount,
  splitShipmentDiscountSchema,
} from "@redotech/redo-fulfillment-settings/split-shipment-discount";
import type { SplitShipmentLocationGroupsView } from "@redotech/redo-fulfillment-settings/split-shipment-location-groups";
import { toast } from "@redotech/redo-web/alert";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMutation } from "@tanstack/react-query";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { SplitShipmentLocationPicker } from "./split-shipment-location-picker";

interface SplitShipmentDiscountFormProps {
  teamId: string;
  initial: SplitShipmentDiscount;
  locations?: SplitShipmentLocationGroupsView;
  locationsError?: string;
  loadingLocations: boolean;
  onReload(): void;
  onSaved(): void;
}

export const SplitShipmentDiscountForm = memo(
  function SplitShipmentDiscountForm({
    teamId,
    initial,
    locations,
    locationsError,
    loadingLocations,
    onReload,
    onSaved,
  }: SplitShipmentDiscountFormProps) {
    const client = useRequiredContext(RedoAdminRpcClientContext);
    const imported =
      !!initial.locationGroups?.length && !!initial.rmrMethods.length;
    const [draft, setDraft] = useState(initial);
    const [editingGroups, setEditingGroups] = useState(!imported);
    const [error, setError] = useState<string>();
    const [confirmedNamesKey, setConfirmedNamesKey] = useState<string>();
    const selected = draft.locationGroups ?? [];
    const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
    const shippingSnapshotUnavailable =
      !locations || loadingLocations || !!locationsError;
    const namesKey = JSON.stringify([locations?.snapshot, selected]);
    const confirmedReservedRateNames = confirmedNamesKey === namesKey;
    const needsNamesConfirmation =
      (editingGroups || draft.enabled) &&
      !!selected.length &&
      locations?.groups.some((group) => group.hasCalculatedRates);
    const mutation = useMutation({
      mutationFn: (settings: SplitShipmentDiscount) =>
        client.updateSplitShipmentDiscount({
          teamId,
          settings,
          shippingSnapshot: locations?.snapshot,
          confirmedReservedRateNames,
        }),
      onSuccess: ({ settings }) => {
        setDraft(settings);
        setEditingGroups(false);
        setError(undefined);
        setConfirmedNamesKey(undefined);
        toast("Split-shipment shipping saved", { variant: "success" });
        onSaved();
      },
      onError: (value) =>
        setError(
          value instanceof Error
            ? value.message
            : "Could not save shipping settings.",
        ),
    });
    const save = (settings: SplitShipmentDiscount) => {
      if ((editingGroups || settings.enabled) && shippingSnapshotUnavailable) {
        setError("Wait for Shopify shipping groups to load before saving.");
        return;
      }
      if (needsNamesConfirmation && !confirmedReservedRateNames) {
        setError("Confirm that the imported names are reserved for Redo.");
        return;
      }
      if (!splitShipmentDiscountSchema.safeParse(settings).success) {
        setError(
          "Enter a percentage from 1 to 100 and select up to 10 location groups.",
        );
        return;
      }
      mutation.mutate(settings);
    };
    const importNames = () => {
      const names =
        locations?.groups
          .filter((group) =>
            selected.some(
              (selection) =>
                selection.profileId === group.profileId &&
                selection.locationGroupId === group.locationGroupId,
            ),
          )
          .flatMap((group) => group.methodNames) ?? [];
      save({
        ...draft,
        enabled: false,
        locationGroups: selected,
        rmrMethods: [...new Set(names)].map((value) => ({
          field: "TITLE",
          operator: "IS",
          value,
        })),
      });
    };
    const discard = () => {
      setDraft(initial);
      setEditingGroups(!imported);
      setError(undefined);
      setConfirmedNamesKey(undefined);
    };
    const savedGroups = locations?.groups.filter((group) =>
      initial.locationGroups?.some(
        (selection) =>
          selection.profileId === group.profileId &&
          selection.locationGroupId === group.locationGroupId,
      ),
    );

    return (
      <Flex dir="column" gap="none">
        <Flex dir="column" gap="xl" px="2xl" py="xl">
          <Flex align="flex-start" gap="xl" justify="space-between" wrap="wrap">
            <Flex
              basis="var(--redo-spacing-11xl)"
              dir="column"
              gap="sm"
              grow="1"
              minW="none"
            >
              <RedoToggle
                disabled={
                  mutation.isPending ||
                  (editingGroups
                    ? "Import the selected groups first."
                    : !imported
                      ? "Import Redo shipping names first."
                      : !draft.enabled && shippingSnapshotUnavailable)
                }
                label="Discount Redo shipping"
                setValue={(enabled) => setDraft({ ...draft, enabled })}
                size="sm"
                value={draft.enabled}
              />
              <Text fontSize="xs" textColor="secondary">
                Applies when an order also ships from a merchant location.
                Redo-only orders keep their normal rate.
              </Text>
            </Flex>
            {imported && !editingGroups && (
              <Flex
                basis="var(--redo-spacing-9xl)"
                dir="column"
                minW="none"
                shrink={0}
              >
                <RedoTextInput
                  ariaLabel="Discount percentage"
                  fullWidth
                  inputTypeHint="number"
                  max={100}
                  min={1}
                  setValue={(value) =>
                    setDraft({ ...draft, percentage: Number(value) })
                  }
                  size="sm"
                  state={mutation.isPending ? "disabled" : undefined}
                  step={1}
                  suffix="%"
                  value={String(draft.percentage)}
                />
              </Flex>
            )}
          </Flex>
          <Flex
            borderColor="secondary"
            borderTopWidth="1px"
            dir="column"
            gap="lg"
            pt="xl"
          >
            <Flex align="center" gap="md" justify="space-between">
              <Text fontSize="xs" fontWeight="semibold">
                Redo shipping groups
              </Text>
              <RedoButton
                disabled={mutation.isPending}
                hierarchy="link"
                onClick={() => {
                  if (!editingGroups) {
                    setEditingGroups(true);
                    setDraft(initial);
                    setError(undefined);
                  }
                  onReload();
                }}
                pending={loadingLocations}
                size="sm"
                text={editingGroups ? "Refresh" : "Change groups"}
              />
            </Flex>
            {locationsError && (
              <RedoAlert
                color="error"
                description={locationsError}
                title="Could not load Shopify groups"
              />
            )}
            {editingGroups ? (
              <>
                <Text fontSize="xs" textColor="secondary">
                  Select the groups fulfilled by Redo. Rates and prices stay in
                  Shopify.
                </Text>
                {locations ? (
                  <SplitShipmentLocationPicker
                    disabled={mutation.isPending}
                    locations={locations}
                    onChange={(locationGroups) =>
                      setDraft({ ...draft, locationGroups })
                    }
                    selected={selected}
                  />
                ) : (
                  !locationsError && (
                    <Text fontSize="xs" textColor="secondary">
                      Loading location groups…
                    </Text>
                  )
                )}
                <Text fontSize="xxs" textColor="tertiary">
                  Importing updates the rate names and turns the discount off
                  for review.
                </Text>
              </>
            ) : imported ? (
              <Flex dir="column" gap="sm">
                <Text fontSize="xs">
                  {savedGroups?.length
                    ? savedGroups
                        .map((group) => group.locationNames.join(", "))
                        .join(" · ")
                    : `${initial.locationGroups?.length ?? 0} location group(s)`}
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  {initial.rmrMethods.map((method) => method.value).join(", ")}
                </Text>
                <Text fontSize="xxs" textColor="tertiary">
                  Keep these rate names exclusive to Redo. Re-import after
                  adding or renaming rates in Shopify.
                </Text>
              </Flex>
            ) : (
              <Text fontSize="xs" textColor="secondary">
                No groups selected.
              </Text>
            )}
          </Flex>
          {needsNamesConfirmation && (
            <RedoCheckbox
              description="Other apps can return names Shopify cannot list in advance. Confirm that every provider, including Conversion, reserves these names for Redo. A collision could discount a merchant shipment."
              disabled={mutation.isPending || shippingSnapshotUnavailable}
              label="I confirmed these shipping-rate names are used only for Redo shipments"
              setValue={(value) =>
                setConfirmedNamesKey(value === true ? namesKey : undefined)
              }
              size="sm"
              value={confirmedReservedRateNames}
            />
          )}
          {error && (
            <RedoAlert
              color="error"
              description={error}
              title="Could not save shipping settings"
            />
          )}
        </Flex>
        {(editingGroups || dirty || mutation.isPending || !!error) && (
          <Flex
            align="center"
            borderColor="secondary"
            borderTopWidth="1px"
            gap="sm"
            justify="flex-end"
            px="2xl"
            py="lg"
          >
            <RedoButton
              disabled={mutation.isPending}
              hierarchy="tertiary"
              onClick={discard}
              size="sm"
              text="Discard"
            />
            <RedoButton
              disabled={
                (needsNamesConfirmation && !confirmedReservedRateNames) ||
                (editingGroups
                  ? shippingSnapshotUnavailable ||
                    (!selected.length && !imported)
                  : !dirty || (draft.enabled && shippingSnapshotUnavailable))
              }
              onClick={() => (editingGroups ? importNames() : save(draft))}
              pending={mutation.isPending}
              size="sm"
              text={editingGroups ? "Import rate names" : "Save discount"}
            />
          </Flex>
        )}
      </Flex>
    );
  },
);
